// TESSERAX — moteur de RELIQUES (méta roguelite façon Balatro).
//
// Une relique = données + crochets (hooks) qui réagissent aux évènements de jeu
// (clear, bascule, combo, perfect, montée de niveau, sortie de Zone). Les
// reliques se cumulent et créent des synergies (ex. un « deck Bascule »).
//
// Ce module est PUR : il ne connaît ni THREE ni le DOM. main.ts fournit une
// implémentation de `RelicApi` (effets concrets : score, charges, jauge…) et
// appelle `emitRelic(...)`. Les fonctions utilitaires (pickOffer, unlock) sont
// testables unitairement.

import { CUMULATIVE_N_CAP } from './constants';

export type RelicRarity = 'commune' | 'rare' | 'épique' | 'légendaire';
// `continuum` = un clear de MAÎTRISE qui PROLONGE le back-to-back (b2b ≥ 2), distinct de
// `combo` (cascade interne) et de la SÉRIE : c'est l'axe « back-to-back » (cf. core/scoring),
// jusqu'ici sans aucune synergie de relique. La charge utile porte la profondeur courante du
// continuum (`count` ≥ 2) et le butin du clear (`gained`).
// `fold` = un Pli joué (mode 'tesseract', échange de tranche active) ; `tessera` = un effacement 4D
// qui a EXIGÉ les deux cubes (cf. core/fold). Charges utiles : fold → {active,folds} ; tessera → {rows,gained}.
export type RelicEvent = 'clear' | 'bascule' | 'combo' | 'perfect' | 'levelup' | 'zoneEnd' | 'continuum' | 'fold' | 'tessera';

// Surface offerte aux reliques par le jeu. `ev` = charge utile de l'évènement.
export interface RelicApi {
  level: number;
  holes: number;            // nb de colonnes trouées (terrain actuel)
  ev: any;                  // payload : {chain,rows,slabs,gained,dir,bankLines…}
  self: Record<string, any>; // mémoire de run propre à la relique (compteurs…)
  // PAS de `rng` : un crochet de relique ne doit JAMAIS tirer de hasard. Le champ existait
  // (jamais utilisé) et main.ts le liait au flux RNG des PIÈCES — la première relique à s'en
  // servir aurait silencieusement désynchronisé la séquence de pièces du défi du jour entre
  // joueurs ET cassé le rejeu bit-exact (anti-triche). Footgun retiré ; au besoin réel un jour,
  // exposer un flux DÉDIÉ seedé (façon relicRng), jamais le flux de pièces.
  // effets
  addScore: (n: number, label?: string) => void;
  addCharge: (n: number) => void;
  addMeter: (n: number) => void;
  extendZone: (ms: number) => void;
  setNextMult: (f: number) => void;   // multiplie le PROCHAIN clear
  bascule: (dir: string) => void;      // re-déclenche une bascule (Miroir)
  // buffs permanents de run (init)
  buffScore: (f: number) => void;
  buffSpeed: (f: number) => void;
  buffMeter: (f: number) => void;
  buffCombo: (f: number) => void;
  buffMaxCharge: (n: number) => void;
  buffLock: (f: number) => void;       // multiplie le lock delay (<1 = plus dur)
  freeHold: () => void;
}

export interface Relic {
  id: string;
  name: string;
  rarity: RelicRarity;
  text: string;             // une ligne lisible (style Balatro)
  tags: string[];           // 'score'|'bascule'|'combo'|'zone'|'risque'…
  unique?: boolean;         // ne peut être pris qu'une fois (défaut: true)
  init?: (api: RelicApi) => void;                  // à l'acquisition
  on?: Partial<Record<RelicEvent, (api: RelicApi) => void>>;
}

const OPPOSITE: Record<string, string> = { nx: 'px', px: 'nx', nz: 'pz', pz: 'nz' };

export const RELICS: Relic[] = [
  // ---- communes : buffs de run (héritières des anciens « atouts ») ----
  { id: 'or', name: 'Tesselles dorées', rarity: 'commune', tags: ['score'],
    text: '+25 % à tous les points gagnés.', init: a => a.buffScore(1.25) },
  { id: 'lest', name: 'Lest', rarity: 'commune', tags: ['contrôle'],
    text: 'Les pièces tombent 18 % plus lentement.', init: a => a.buffSpeed(1.18) },
  { id: 'aimant', name: 'Aimant de Zone', rarity: 'commune', tags: ['zone'],
    text: 'La jauge de Zone se remplit 40 % plus vite.', init: a => a.buffMeter(1.4) },
  { id: 'metronome', name: 'Métronome', rarity: 'commune', tags: ['combo'],
    text: 'Bonus d’enchaînement renforcé (×1.7).', init: a => a.buffCombo(1.7) },
  { id: 'gyro', name: 'Gyroscope', rarity: 'commune', tags: ['bascule'],
    text: '+1 charge de Bascule maximum.', init: a => a.buffMaxCharge(1) },
  { id: 'reserve', name: 'Réserve libre', rarity: 'commune', tags: ['contrôle'],
    text: 'La réserve (Hold) devient illimitée.', init: a => a.freeHold() },
  { id: 'amortisseur', name: 'Amortisseur', rarity: 'commune', tags: ['contrôle'],
    text: 'Délai de verrouillage allongé de 40 % (placement plus serein).', init: a => a.buffLock(1.4) },
  { id: 'prisme', name: 'Prisme', rarity: 'commune', tags: ['score', 'zone'],
    text: '+12 % aux points et +20 % au remplissage de Zone.', init: a => { a.buffScore(1.12); a.buffMeter(1.2); } },
  { id: 'catalyseur', name: 'Catalyseur', rarity: 'commune', tags: ['combo', 'zone'],
    text: 'Chaque enchaînement recharge un peu la Zone.',
    on: { combo: a => a.addMeter(3) } },
  { id: 'rouage', name: 'Rouage', rarity: 'commune', tags: ['bascule', 'combo'],
    text: '+1 charge de Bascule maximum et bonus d’enchaînement (×1.3).',
    init: a => { a.buffMaxCharge(1); a.buffCombo(1.3); } },
  { id: 'silex', name: 'Silex', rarity: 'commune', tags: ['score'],
    text: 'Chaque effacement rapporte un petit bonus fixe (+45 × niveau).',
    on: { clear: a => { if (((a.ev.rows || 0) + (a.ev.slabs || 0)) > 0) a.addScore(45 * a.level, 'Silex'); } } },
  { id: 'fondation', name: 'Fondation', rarity: 'commune', tags: ['zone', 'contrôle'],
    text: 'Chaque palier de profondeur regonfle un peu la jauge de Zone.',
    on: { levelup: a => a.addMeter(22) } },

  // ---- rares : crochets évènementiels (le « deck Bascule ») ----
  { id: 'avalanche', name: 'Avalanche', rarity: 'rare', tags: ['bascule', 'combo'],
    text: 'Une Bascule qui casse ≥2 lignes booste le prochain clear (+×0.6).',
    on: { bascule: a => { if (a.ev && (a.ev.rows + a.ev.slabs) >= 2) { a.setNextMult(1.6); a.addScore(0, 'Avalanche'); } } } },
  { id: 'tectonique', name: 'Tectonique', rarity: 'rare', tags: ['bascule'],
    text: 'Basculer un terrain très troué (≥10 trous) rembourse une charge.',
    on: { bascule: a => { if (a.holes >= 10) a.addCharge(1); } } },
  { id: 'resonance', name: 'Résonance', rarity: 'rare', tags: ['combo', 'bascule'],
    text: 'Tous les 4 enchaînements, rembourse une charge de Bascule.',
    on: { combo: a => { a.self.n = (a.self.n || 0) + 1; if (a.self.n % 4 === 0) a.addCharge(1); } } },
  { id: 'faille', name: 'Faille', rarity: 'rare', tags: ['zone', 'bascule'],
    text: 'Un gros effacement (≥3 lignes) donne une charge de plus et prolonge la Zone.',
    on: { clear: a => { if ((a.ev.rows || 0) >= 3) { a.addCharge(1); a.extendZone(1000); } } } },
  { id: 'treuil', name: 'Treuil', rarity: 'rare', tags: ['contrôle', 'bascule'],
    text: 'Chaque palier de profondeur rembourse une charge de Bascule.',
    on: { levelup: a => a.addCharge(1) } },
  { id: 'sourcier', name: 'Sourcier', rarity: 'rare', tags: ['zone'],
    text: 'Les clears multi-lignes (≥2) gonflent fortement la Zone.',
    on: { clear: a => { if (a.ev.rows >= 2) a.addMeter(a.ev.rows * 4); } } },
  { id: 'pendule', name: 'Pendule', rarity: 'rare', tags: ['bascule', 'combo'],
    text: 'Une Bascule de PRÉPARATION (sans clear) booste le prochain clair (+×0.4).',
    on: { bascule: a => { if (a.ev && (a.ev.rows + a.ev.slabs) === 0 && a.ev.moved > 0) a.setNextMult(1.4); } } },
  { id: 'echo', name: 'Écho', rarity: 'rare', tags: ['combo', 'score'],
    // BAL-DOMINANT-001 : Écho était le 2ᵉ crochet ADDITIF le plus puissant (mesuré ~360 k/run, cf.
    // docs/balance-baseline.md) — la série n'étant pas bornée, le terme série×niveau enflait sans limite.
    // On rabote le coef (40 → 28) ET on plafonne la série prise en compte (≤ 12) : Écho reste un fort
    // moteur de combo, sans écraser les autres archétypes. Aucune autre relique touchée.
    text: 'Chaque enchaînement rapporte un bonus croissant (+28 × série × niveau, série plafonnée).',
    on: { combo: a => { const s = Math.min((a.ev && a.ev.streak) || 0, 12); if (s > 0) a.addScore(s * 28 * a.level, 'Écho'); } } },
  { id: 'sablier', name: 'Sablier', rarity: 'rare', tags: ['zone', 'bascule'],
    text: 'Sortir de Zone avec ≥2 lignes en banque rend une charge de Bascule.',
    on: { zoneEnd: a => { if (((a.ev && a.ev.bankLines) || 0) >= 2) a.addCharge(1); } } },
  { id: 'gisement', name: 'Gisement', rarity: 'rare', tags: ['zone', 'risque'],
    text: 'Sur terrain troué (≥8 trous), les clears regonflent fortement la Zone.',
    on: { clear: a => { if (a.holes >= 8 && a.ev.rows > 0) a.addMeter(a.ev.rows * 5); } } },
  { id: 'gravite', name: 'Puits gravitationnel', rarity: 'rare', tags: ['risque', 'score'],
    text: 'Effacer sur un puits dangereux (≥6 colonnes trouées) rapporte +150/ligne × niveau.',
    on: { clear: a => { const lines = (a.ev.rows || 0) + (a.ev.slabs || 0); if (a.holes >= 6 && lines > 0) a.addScore(lines * 150 * a.level, 'Gravité'); } } },
  { id: 'palier', name: 'Palier', rarity: 'rare', tags: ['score'],
    text: 'Chaque palier de profondeur rapporte un bonus croissant (+320 × niveau).',
    on: { levelup: a => a.addScore(320 * a.level, 'Palier') } },
  { id: 'equilibre', name: 'Équilibre', rarity: 'rare', tags: ['contrôle', 'score'],
    text: 'Effacer sur un puits SANS trou rapporte +130/ligne × niveau (récompense le jeu propre).',
    on: { clear: a => { if (a.holes === 0 && (a.ev.rows || 0) > 0) a.addScore(a.ev.rows * 130 * a.level, 'Équilibre'); } } },
  { id: 'croisement', name: 'Croisement', rarity: 'rare', tags: ['score', 'combo'],
    text: 'Chaque Croix (deux axes effacés d’un coup) rapporte un gros bonus (+420 × niveau).',
    on: { clear: a => { if ((a.ev.cross || 0) > 0) a.addScore(a.ev.cross * 420 * a.level, 'Croisement'); } } },
  // ---- deck CROIX : récompense la CROIX, le feat propre au puits 3D (deux axes horizontaux
  // effacés d'un coup, impossible en 2D). Jusqu'ici un seul crochet (`croisement`) y touchait :
  // aucune RAISON DE CONSTRUIRE autour de la Croix. Ce deck ouvre l'archétype — ressource
  // (Quadrant), maîtrise cumulée (Méridien), apogée (Mandala) — pour que poser une Croix DEVIENNE
  // une stratégie, pas un coup chanceux. PUR : score/charge/Zone/nextMult uniquement, aucun RNG. ----
  { id: 'quadrant', name: 'Quadrant', rarity: 'rare', tags: ['croix', 'bascule', 'zone'],
    text: 'Chaque Croix rend une charge de Bascule et prolonge la Zone (la Croix nourrit tes ressources).',
    on: { clear: a => { if ((a.ev.cross || 0) > 0) { a.addCharge(1); a.extendZone(900); } } } },
  // Porte d'entrée de la Voie de la Croix (palier I, cf. core/voies) : un moteur doux qui donne
  // une raison de CHERCHER la Croix dès les premières parties (la jauge passe par addMeter,
  // bloquée pendant la Zone → jamais de perma-Zone par construction).
  { id: 'rosace', name: 'Rosace', rarity: 'commune', tags: ['croix', 'zone'],
    text: 'Chaque Croix regonfle la Zone (+8 par Croix).',
    on: { clear: a => { if ((a.ev.cross || 0) > 0) a.addMeter(8 * a.ev.cross); } } },
  // ---- deck CONTINUUM : récompense le back-to-back de maîtrise (axe neuf, cf. core/scoring).
  // Le tag `continuum` matérialise le deck (synergie de draft + Voie du Continuum, cf. core/voies) —
  // il n'existait qu'en commentaire, aucune relique ne le portait. ----
  { id: 'elan', name: 'Élan', rarity: 'rare', tags: ['continuum', 'combo', 'score'],
    text: 'Chaque clear de CONTINUUM (back-to-back) rapporte un bonus croissant (+120 × continuum × niveau).',
    on: { continuum: a => { const n = a.ev.count || 0; if (n >= 2) a.addScore(n * 120 * a.level, 'Élan'); } } },
  { id: 'relais', name: 'Relais', rarity: 'rare', tags: ['continuum', 'bascule', 'combo'],
    text: 'Tous les 3 clears de CONTINUUM, rembourse une charge de Bascule (le back-to-back nourrit la Bascule).',
    on: { continuum: a => { a.self.n = (a.self.n || 0) + 1; if (a.self.n % 3 === 0) a.addCharge(1); } } },
  { id: 'sillage', name: 'Sillage', rarity: 'commune', tags: ['continuum', 'zone'],
    text: 'Chaque clear de CONTINUUM regonfle un peu la Zone (+6).',
    on: { continuum: a => a.addMeter(6) } },

  // ---- épiques ----
  { id: 'chaine_or', name: 'Chaîne d’or', rarity: 'épique', tags: ['combo', 'score'],
    text: 'Chaque clair en enchaînement (chaîne ≥2) rapporte un bonus croissant.',
    on: { clear: a => { if (a.ev.chain >= 2) a.addScore(Math.round(a.ev.chain * 120 * a.level), 'Chaîne d’or'); } } },
  // TEXTES DE CHARGE — VÉRITÉ DU PLAFOND (cf. constants.RELIC_CHARGE_PER_EVENT, abaissé 2 → 1) :
  // les reliques ci-dessous promettaient « 2 charges » que le budget par évènement clampait à 1
  // depuis l'abaissement — texte ET effet sont réalignés sur 1 (addCharge(1) : identique bit à
  // bit, le clamp donnait déjà 1). Ne jamais annoncer plus que le budget d'un évènement.
  { id: 'perfectionniste', name: 'Perfectionniste', rarity: 'épique', tags: ['risque', 'score'],
    text: 'Une cascade profonde (chaîne ≥ 5) rapporte gros (+2500 × niveau) et rend 1 charge.',
    on: { clear: a => { if ((a.ev.chain || 0) >= 5) { a.addScore(2500 * a.level, 'Perfectionniste'); a.addCharge(1); } } } },
  { id: 'sismographe', name: 'Sismographe', rarity: 'épique', tags: ['bascule', 'score'],
    text: 'Score de la Bascule selon le nombre de cases déplacées (+10/case).',
    on: { bascule: a => { const m = (a.ev && a.ev.moved) || 0; if (m > 0) a.addScore(m * 10 * a.level, 'Sismographe'); } } },
  { id: 'crescendo', name: 'Crescendo', rarity: 'épique', tags: ['zone', 'score'],
    // BAL-DOMINANT-001 : Crescendo était le 1ᵉʳ crochet ADDITIF (mesuré ~410 k/run) — il amplifie le
    // « zone-stall » (plus on banke de lignes, plus il paie, × niveau). On rabote 180 → 120 : la sortie
    // de Zone reste un gros pic, mais cesse d'être le levier de score n°1 à lui seul. (Réversible.)
    text: 'La sortie de Zone rapporte +120 par ligne mise en banque × niveau.',
    on: { zoneEnd: a => { const b = (a.ev && a.ev.bankLines) || 0; if (b > 0) a.addScore(b * 120 * a.level, 'Crescendo'); } } },
  { id: 'fractale', name: 'Fractale', rarity: 'épique', tags: ['combo', 'zone'],
    text: 'Les longs enchaînements gonflent la Zone et, tous les 4, rendent une charge.',
    on: { combo: a => { const s = (a.ev && a.ev.streak) || 0; a.addMeter(s * 2); if (s > 0 && s % 4 === 0) a.addCharge(1); } } },
  { id: 'resonateur', name: 'Résonateur', rarity: 'épique', tags: ['bascule', 'combo'],
    text: 'Une Bascule qui déclenche une cascade (chaîne ≥2) rend une charge et booste le prochain clair.',
    on: { bascule: a => { if (a.ev && a.ev.chain >= 2) { a.addCharge(1); a.setNextMult(1.5); } } } },
  { id: 'gravats', name: 'Champ de gravats', rarity: 'épique', tags: ['risque', 'score'],
    text: 'Chaque clair rapporte selon les trous du terrain (+60 × trou × niveau).',
    on: { clear: a => { if (a.ev.rows > 0 && a.holes > 0) a.addScore(a.holes * 60 * a.level, 'Gravats'); } } },
  { id: 'aubaine', name: 'Aubaine', rarity: 'épique', tags: ['score'],
    text: 'Les 4 premiers paliers de profondeur rapportent un gros bonus (+900 × niveau).',
    on: { levelup: a => { a.self.n = (a.self.n || 0) + 1; if (a.self.n <= 4) a.addScore(900 * a.level, 'Aubaine'); } } },
  { id: 'onde_choc', name: 'Onde de choc', rarity: 'épique', tags: ['bascule', 'risque'],
    text: 'Une Bascule qui efface ≥3 lignes rend 1 charge ET double le prochain clair.',
    on: { bascule: a => { if (a.ev && ((a.ev.rows || 0) + (a.ev.slabs || 0)) >= 3) { a.addCharge(1); a.setNextMult(2); } } } },
  { id: 'harmonique', name: 'Harmoniques', rarity: 'épique', tags: ['combo', 'zone'],
    text: 'Au-delà de la série ×3, chaque enchaînement supplémentaire rapporte et gonfle la Zone.',
    on: { combo: a => { const s = (a.ev && a.ev.streak) || 0; if (s >= 4) { a.addScore((s - 3) * 90 * a.level, 'Harmoniques'); a.addMeter((s - 3) * 3); } } } },
  { id: 'vortex', name: 'Vortex', rarity: 'épique', tags: ['zone', 'combo'],
    text: 'Chaque enchaînement pendant la Zone prolonge le temps figé (+0,45 s).',
    on: { combo: a => a.extendZone(450) } },
  { id: 'apogee', name: 'Apogée', rarity: 'épique', tags: ['continuum', 'combo', 'zone', 'score'],
    text: 'Un CONTINUUM profond (≥4) sur-multiplie le prochain clair (×1.6) et gonfle la Zone.',
    on: { continuum: a => { if ((a.ev.count || 0) >= 4) { a.setNextMult(1.6); a.addMeter(8); } } } },
  // deck CROIX (suite) — maîtrise CUMULÉE : chaque Croix de la partie paie davantage que la
  // précédente (+220 × Nᵉ Croix × niveau), à la manière d'Aubaine mais CROISSANT au lieu de
  // décroissant → le joueur qui s'engage dans le jeu en Croix est récompensé de sa constance.
  { id: 'meridien', name: 'Méridien', rarity: 'épique', tags: ['croix', 'score'],
    // N plafonné (CUMULATIVE_N_CAP) : anti-abus TAS sur le classement (cf. docs/balance-baseline.md) ;
    // sans effet à cadence humaine (~30 Croix/run, loin sous le plafond).
    text: 'Chaque Croix rapporte un bonus qui CROÎT avec le nombre de Croix réussies dans la partie (+220 × Nᵉ Croix × niveau, N plafonné).',
    on: { clear: a => { if ((a.ev.cross || 0) > 0) { a.self.n = (a.self.n || 0) + 1; a.addScore(Math.min(a.self.n, CUMULATIVE_N_CAP) * 220 * a.level, 'Méridien'); } } } },

  // ---- légendaires : changent les règles ----
  { id: 'verre', name: 'Canon de verre', rarity: 'légendaire', tags: ['risque', 'score'],
    text: 'Score ×2.0, mais le délai de verrouillage est divisé par deux.',
    init: a => { a.buffScore(2.0); a.buffLock(0.5); } },
  { id: 'miroir', name: 'Miroir', rarity: 'légendaire', tags: ['bascule'],
    text: 'Chaque Bascule se répercute aussi vers le mur opposé.',
    on: { bascule: a => { if (a.ev && !a.ev.mirrored && OPPOSITE[a.ev.dir]) { a.ev.mirrored = true; a.bascule(OPPOSITE[a.ev.dir]); } } } },
  { id: 'tesseract', name: 'Tesseract', rarity: 'légendaire', tags: ['score', 'combo'],
    text: 'Score ×1.5 ; chaque cascade profonde (chaîne ≥3) rend une charge et un bonus.',
    init: a => a.buffScore(1.5),
    on: { clear: a => { if (a.ev.chain >= 3) { a.addCharge(1); a.addScore(a.ev.chain * 200 * a.level, 'Tesseract'); } } } },
  { id: 'antimatiere', name: 'Antimatière', rarity: 'légendaire', tags: ['risque', 'score'],
    text: 'Score ×2.6, mais les pièces tombent 18 % plus vite.',
    init: a => { a.buffScore(2.6); a.buffSpeed(0.82); } },
  { id: 'noyau', name: 'Noyau dur', rarity: 'légendaire', tags: ['zone', 'score'],
    text: 'Un effacement de ≥4 lignes sur-multiplie le prochain clair (×1.8) et regonfle la Zone.',
    on: { clear: a => { if ((a.ev.rows || 0) >= 4) { a.setNextMult(1.8); a.addMeter(10); } } } },
  // deck CROIX (apogée) — la Croix DOUBLE (les deux axes effacés sur ≥2 rangées chacun → cross ≥2)
  // est l'expression la plus rare et la plus maîtrisée du puits 3D : elle déclenche un apogée
  // (gros bonus, 1 charge, prochain clair ×1.8). Score ×1.4 de base pour ancrer le build.
  { id: 'mandala', name: 'Mandala', rarity: 'légendaire', tags: ['croix', 'score', 'zone'],
    text: 'Score ×1.4 ; une Croix DOUBLE (les deux axes sur ≥2 rangées) déclenche un apogée : +1800 × niveau, 1 charge et prochain clair ×1.8.',
    init: a => a.buffScore(1.4),
    on: { clear: a => { if ((a.ev.cross || 0) >= 2) { a.addScore(1800 * a.level, 'Mandala'); a.addCharge(1); a.setNextMult(1.8); } } } },
  { id: 'prophetie', name: 'Prophétie', rarity: 'légendaire', tags: ['zone', 'score', 'combo'],
    text: 'Sortir de Zone relance un quart de la jauge ET booste le prochain clair (×1.5).',
    on: { zoneEnd: a => { a.addMeter(25); a.setNextMult(1.5); } } },
  { id: 'obsidienne', name: 'Cœur d’obsidienne', rarity: 'légendaire', tags: ['risque', 'bascule', 'score'],
    text: 'Score ×1.8 et +1 charge de Bascule maximum, mais le délai de verrouillage est raccourci de 22 %.',
    init: a => { a.buffScore(1.8); a.buffMaxCharge(1); a.buffLock(0.78); } },
  // ---- deck PLI (4D) : synergies autour du Pli et de la Tessera (mode 'tesseract', cf. core/fold) ----
  { id: 'charniere', name: 'Charnière', rarity: 'rare', tags: ['pli', 'bascule'],
    text: 'Chaque TESSERAX (effacement 4D) rembourse une charge.',
    on: { tessera: a => a.addCharge(1) } },
  // `origami`/`moebius` : PREMIERS crochets de l'évènement `fold` (émis depuis toujours par
  // rules.ts/sim.ts, zéro relique ne l'écoutait) → le geste du Pli lui-même devient un moteur,
  // pas seulement son couronnement (la Tessera). Paliers II/IV de la Voie du Pli (cf. core/voies).
  { id: 'origami', name: 'Origami', rarity: 'rare', tags: ['pli', 'combo'],
    text: 'Chaque PLI joué aiguise le prochain clair (+×0.25).',
    on: { fold: a => a.setNextMult(1.25) } },
  { id: 'moebius', name: 'Ruban de Möbius', rarity: 'épique', tags: ['pli', 'bascule'],
    text: 'Tous les 3 PLIS joués, rembourse une charge (le Pli nourrit la réserve ◆).',
    on: { fold: a => { a.self.n = (a.self.n || 0) + 1; if (a.self.n % 3 === 0) a.addCharge(1); } } },
  { id: 'palimpseste', name: 'Palimpseste', rarity: 'épique', tags: ['pli', 'score'],
    text: 'Chaque TESSERAX rapporte un bonus CROISSANT (+500 × Nᵉ TESSERAX × niveau, N plafonné).',
    on: { tessera: a => { a.self.n = (a.self.n || 0) + 1; a.addScore(Math.min(a.self.n, CUMULATIVE_N_CAP) * 500 * a.level, 'Palimpseste'); } } },
  { id: 'syzygie', name: 'Syzygie', rarity: 'légendaire', tags: ['pli', 'score', 'combo'],
    text: 'Score ×1.4 ; une TESSERAX double le prochain clair et regonfle la Zone.',
    init: a => a.buffScore(1.4),
    on: { tessera: a => { a.setNextMult(2); a.addMeter(12); } } },
  // Clé de voûte de la Voie du Continuum (palier V, cf. core/voies) : le deck n'avait pas
  // d'apogée légendaire propre (Élan/Relais/Apogée s'arrêtaient à l'épique). Crochet ACTIF
  // (continuum profond ≥5, dur à tenir) → paie plus qu'un passif, conforme à balance-baseline.
  { id: 'ligne_de_monde', name: 'Ligne de monde', rarity: 'légendaire', tags: ['continuum', 'score'],
    text: 'Score ×1.35 ; un CONTINUUM profond (≥5) rapporte +700 × niveau et rend une charge.',
    init: a => a.buffScore(1.35),
    on: { continuum: a => { if ((a.ev.count || 0) >= 5) { a.addScore(700 * a.level, 'Ligne de monde'); a.addCharge(1); } } } },
  // ---- PACTES (bonus + CONTREPARTIE) : la contrepartie est un buff <1 sur un levier existant,
  // donc bit-exacte vif↔sim par construction (une seule définition data-driven, exécutée par les
  // deux moteurs via RelicApi) et bornée par les plafonds additifs (RUN_SCORE_MULT_CAP / NEXT_MULT_CAP,
  // plancher 0.1 de stackNextMult). Socle libre (pas de Voie : l'invariant « palier final légendaire »
  // de core/voies reste intact) ; Canon de verre / Antimatière sont les aînés de la famille. ----
  { id: 'monolithe', name: 'Monolithe', rarity: 'épique', tags: ['risque', 'score'],
    text: '+50 % à tous les points, mais le bonus d’enchaînement est presque éteint.',
    init: a => { a.buffScore(1.5); a.buffCombo(0.35); } },
  { id: 'serment_de_plomb', name: 'Serment de plomb', rarity: 'épique', tags: ['risque', 'zone'],
    text: '+45 % aux points, mais la jauge de Zone se remplit deux fois plus lentement.',
    init: a => { a.buffScore(1.45); a.buffMeter(0.5); } },
  { id: 'usurier', name: 'Usurier', rarity: 'rare', tags: ['risque', 'bascule'],
    text: '+2 charges de Bascule maximum, mais chaque Bascule émousse le prochain clair (−×0.2).',
    init: a => a.buffMaxCharge(2),
    on: { bascule: a => a.setNextMult(0.8) } },
  { id: 'funambule', name: 'Funambule', rarity: 'légendaire', tags: ['risque', 'contrôle', 'score'],
    text: 'Réserve illimitée et +30 % aux points, mais le verrou tombe 40 % plus vite.',
    init: a => { a.freeHold(); a.buffScore(1.3); a.buffLock(0.6); } },
];

export function relicById(id: string): Relic | undefined { return RELICS.find(r => r.id === id); }

// Mélange déterministe (Fisher-Yates) avec une RNG fournie (graine du jour).
function shuffle<T>(arr: T[], rng: () => number): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) { const j = (rng() * (i + 1)) | 0; const t = a[i]; a[i] = a[j]; a[j] = t; }
  return a;
}

// Propose `n` reliques tirées du pool débloqué, en excluant les uniques déjà
// possédées. Déterministe via `rng` (rejouabilité du défi du jour).
export function pickOffer(unlockedIds: string[], ownedIds: string[], rng: () => number, n = 3): Relic[] {
  const owned = new Set(ownedIds);
  const pool = RELICS.filter(r => unlockedIds.includes(r.id) && !(r.unique !== false && owned.has(r.id)));
  return shuffle(pool, rng).slice(0, n);
}

// ---- DRAFT (identité de run) ---------------------------------------------------
// `pickOffer` tire à PLAT : chaque relique débloquée a la même chance, à toute
// profondeur, quel que soit le build en cours. Le choix « 1 parmi 3 » existe déjà,
// mais il SUBIT le hasard au lieu d'exprimer une décision. La DRAFT corrige cela
// sans toucher l'acquisition : le tirage est pondéré pour que (1) les raretés MONTENT
// avec la profondeur — communes tôt, épique/légendaire tard → un crescendo lisible —
// et (2) une relique qui PROLONGE un archétype déjà entamé (tags partagés) remonte →
// les builds se CONSTRUISENT. PUR et déterministe via `rng` (défi du jour intact).

const RARITY_ORDER: RelicRarity[] = ['commune', 'rare', 'épique', 'légendaire'];
// Rang numérique d'une rareté : 0 commune … 3 légendaire (−∞ borné à 0 si inconnu).
export function rarityRank(r: RelicRarity): number { const i = RARITY_ORDER.indexOf(r); return i < 0 ? 0 : i; }

// Tags du build courant (union des tags des reliques déjà possédées) — l'entrée de
// la pondération de SYNERGIE. Utilitaire pour main.ts.
export function ownedTagsOf(relics: Relic[]): string[] {
  const s = new Set<string>();
  for (const r of relics) for (const t of r.tags) s.add(t);
  return Array.from(s);
}

// Poids de tirage d'une relique dans la DRAFT. Combine une COURBE DE RARETÉ par
// profondeur (la commune décroît, les raretés montent par paliers) et un BONUS DE
// SYNERGIE multiplicatif par tag partagé avec le build. Un plancher garde chaque
// rareté atteignable (jamais 0 → le coup de chance reste possible).
export function relicDraftWeight(r: Relic, ctx: { level: number; ownedTags?: string[] }): number {
  const lv = Math.max(1, ctx.level | 0);
  // Courbe par rareté (indexée par rarityRank). Réglée pour que la profondeur ~2 reste
  // surtout commune et que l'épique/légendaire ne décolle qu'en milieu/fin de run.
  const curve = [
    Math.max(0.5, 2.6 - lv * 0.16),    // commune : forte tôt, s'estompe
    0.7 + Math.min(lv, 12) * 0.12,     // rare : monte puis plafonne
    Math.max(0.12, (lv - 3) * 0.16),   // épique : n'émerge qu'après ~niv. 3
    Math.max(0.06, (lv - 6) * 0.13),   // légendaire : tardive, rare
  ];
  let w = curve[rarityRank(r.rarity)] || 0.1;
  const owned = ctx.ownedTags;
  if (owned && owned.length) {
    let shared = 0;
    for (const t of r.tags) if (owned.includes(t)) shared++;
    if (shared > 0) w *= 1 + Math.min(shared, 3) * 0.6;   // +60 % par tag partagé (borné à 3)
  }
  return Math.max(0.02, w);
}

// Tirage pondéré SANS remise, déterministe via `rng` (roulette cumulée).
function weightedSample<T>(items: T[], weight: (x: T) => number, rng: () => number, n: number): T[] {
  const pool = items.slice();
  const out: T[] = [];
  while (out.length < n && pool.length) {
    let total = 0; for (const x of pool) total += Math.max(0, weight(x));
    // Tous les poids ≤0 (ne survient pas en jeu : relicDraftWeight ≥0.02) : on consomme QUAND MÊME
    // le RNG pour rester déterministe (un pool.shift() sans tirage divergerait du chemin pondéré).
    if (!(total > 0)) { const idx = Math.min(pool.length - 1, Math.floor(rng() * pool.length)); out.push(pool[idx]); pool.splice(idx, 1); continue; }
    let pick = rng() * total, idx = 0;
    for (; idx < pool.length; idx++) { pick -= Math.max(0, weight(pool[idx])); if (pick <= 0) break; }
    if (idx >= pool.length) idx = pool.length - 1;
    out.push(pool[idx]); pool.splice(idx, 1);
  }
  return out;
}

// DRAFT de reliques : même filtre que `pickOffer` (débloqué, non-possédé si unique),
// mais le tirage est PONDÉRÉ par profondeur (raretés croissantes) et SYNERGIE (tags du
// build). Chaque choix pèse → un build s'affirme au fil de la partie.
export function draftOffer(unlockedIds: string[], ownedIds: string[], rng: () => number,
                           ctx: { level: number; ownedTags?: string[] }, n = 3): Relic[] {
  const owned = new Set(ownedIds);
  const pool = RELICS.filter(r => unlockedIds.includes(r.id) && !(r.unique !== false && owned.has(r.id)));
  return weightedSample(pool, r => relicDraftWeight(r, ctx), rng, n);
}

// Reliques débloquées par défaut (les communes + 2 rares) ; le reste se gagne
// via les jalons de méta inter-parties.
export const STARTER_UNLOCKS = ['or', 'lest', 'aimant', 'metronome', 'gyro', 'reserve', 'amortisseur', 'prisme', 'catalyseur', 'rouage', 'silex', 'fondation', 'avalanche', 'tectonique', 'charniere'];

// Stats cumulées qui pilotent les déblocages (cf. core/meta.MetaStats). Les métriques de
// PRATIQUE (`croix`/`tesseras`/`continuum`/`zoneLines`) portent les VOIES (cf. core/voies) :
// on débloque le deck de la Croix EN réussissant des Croix, le deck du Pli EN pliant, etc.
export type UnlockMetric = 'runs' | 'bascules' | 'bestScore' | 'croix' | 'tesseras' | 'continuum' | 'zoneLines';
// `or` = chemins de déblocage ALTERNATIFS (MOT-MIDTERM-001, devenu une LISTE avec les Voies) :
// une relique gardée par sa métrique de pratique conserve TOUS ses anciens chemins (expérience
// runs/bascules, bestScore) en alternatives → aucun joueur en cours de progression ne recule
// (invariant testé : unlocksFor(anciennes stats) ⊇ ancien résultat). C'est purement un chemin
// d'ACCÈS PLUS TÔT : l'effet de la relique, les plafonds de puissance (RUN_SCORE_MULT_CAP…)
// et le tirage 1-parmi-3 sont INCHANGÉS → plus de VARIÉTÉ de draft, jamais plus de puissance.
export interface UnlockPath { metric: UnlockMetric; threshold: number; }
export interface UnlockReq extends UnlockPath { or?: UnlockPath[]; }
export type UnlockStats = { runs?: number; bascules?: number; perfects?: number; bestScore?: number; croix?: number; tesseras?: number; continuum?: number; zoneLines?: number };

// SOURCE UNIQUE des conditions de déblocage (hors STARTER_UNLOCKS) : `unlocksFor`
// en DÉRIVE, et l'UI de collection l'EXPOSE (reliques verrouillées + barre de
// progression). Avant, ces seuils n'existaient qu'enfouis dans des `if` → le joueur
// ne pouvait NI savoir qu'une relique existe NI viser sa condition. Garder cette
// table et `unlocksFor` synchronisées est garanti par construction (un seul endroit).
// Échelle de progression : chaque jalon ouvre un archétype de build supplémentaire
// (moteur de Zone, deck de Bascule, deck Croix/Continuum/Pli, all-in de score).
// Les reliques d'une VOIE (cf. core/voies) portent leur métrique de PRATIQUE en chemin
// principal — seuils croissants par palier — et leurs ANCIENS seuils en alternatives `or`
// (invariant de migration : jamais plus restrictif qu'avant). Le « socle libre » (score /
// contrôle / risque génériques) reste à l'expérience et au bestScore, inchangé.
export const UNLOCK_REQS: Record<string, UnlockReq> = {
  // VOIE DE LA BASCULE — la pratique de la Bascule ouvre ses propres synergies (échelle historique).
  resonance:       { metric: 'bascules', threshold: 25 },
  pendule:         { metric: 'bascules', threshold: 40 },
  sablier:         { metric: 'bascules', threshold: 50 },
  sismographe:     { metric: 'bascules', threshold: 60 },
  resonateur:      { metric: 'bascules', threshold: 80 },
  onde_choc:       { metric: 'bascules', threshold: 100 },
  miroir:          { metric: 'bascules', threshold: 160, or: [{ metric: 'bestScore', threshold: 120000 }] },
  // VOIE DE LA CROIX — débloquée EN réussissant des Croix.
  rosace:          { metric: 'croix', threshold: 2,  or: [{ metric: 'runs', threshold: 5 }] },
  croisement:      { metric: 'croix', threshold: 6,  or: [{ metric: 'runs', threshold: 9 }] },
  quadrant:        { metric: 'croix', threshold: 14, or: [{ metric: 'runs', threshold: 14 }] },
  meridien:        { metric: 'croix', threshold: 30, or: [{ metric: 'bestScore', threshold: 95000 }, { metric: 'bascules', threshold: 130 }] },
  mandala:         { metric: 'croix', threshold: 60, or: [{ metric: 'bestScore', threshold: 175000 }] },
  // VOIE DU CONTINUUM — débloquée EN tenant des back-to-backs.
  sillage:         { metric: 'continuum', threshold: 3,  or: [{ metric: 'runs', threshold: 6 }] },
  elan:            { metric: 'continuum', threshold: 10, or: [{ metric: 'runs', threshold: 11 }] },
  relais:          { metric: 'continuum', threshold: 22, or: [{ metric: 'runs', threshold: 13 }] },
  apogee:          { metric: 'continuum', threshold: 45, or: [{ metric: 'bestScore', threshold: 85000 }, { metric: 'runs', threshold: 24 }] },
  ligne_de_monde:  { metric: 'continuum', threshold: 90, or: [{ metric: 'bestScore', threshold: 140000 }] },
  // VOIE DU PLI — débloquée EN réussissant des TESSERAX (charnière reste starter : la porte d'entrée).
  origami:         { metric: 'tesseras', threshold: 3,  or: [{ metric: 'runs', threshold: 8 }] },
  palimpseste:     { metric: 'tesseras', threshold: 9,  or: [{ metric: 'runs', threshold: 6 }] },
  moebius:         { metric: 'tesseras', threshold: 20, or: [{ metric: 'runs', threshold: 20 }] },
  syzygie:         { metric: 'tesseras', threshold: 40, or: [{ metric: 'bestScore', threshold: 120000 }] },
  // VOIE DU FLUX — débloquée EN banquant des lignes en Zone (sorties de Zone).
  faille:          { metric: 'zoneLines', threshold: 10,  or: [{ metric: 'runs', threshold: 3 }] },
  sourcier:        { metric: 'zoneLines', threshold: 18,  or: [{ metric: 'runs', threshold: 8 }] },
  gisement:        { metric: 'zoneLines', threshold: 30,  or: [{ metric: 'runs', threshold: 10 }] },
  fractale:        { metric: 'zoneLines', threshold: 45,  or: [{ metric: 'runs', threshold: 12 }] },
  harmonique:      { metric: 'zoneLines', threshold: 60,  or: [{ metric: 'runs', threshold: 12 }] },
  vortex:          { metric: 'zoneLines', threshold: 80,  or: [{ metric: 'bestScore', threshold: 70000 }, { metric: 'runs', threshold: 20 }] },
  crescendo:       { metric: 'zoneLines', threshold: 100, or: [{ metric: 'bestScore', threshold: 75000 }, { metric: 'runs', threshold: 22 }] },
  prophetie:       { metric: 'zoneLines', threshold: 160, or: [{ metric: 'bestScore', threshold: 160000 }] },
  // SOCLE LIBRE — moteurs génériques (score / contrôle / risque), à l'expérience : inchangés.
  treuil:          { metric: 'runs', threshold: 4 },
  palier:          { metric: 'runs', threshold: 4 },
  echo:            { metric: 'runs', threshold: 5 },
  chaine_or:       { metric: 'runs', threshold: 6 },
  equilibre:       { metric: 'runs', threshold: 7 },
  gravite:         { metric: 'runs', threshold: 10 },
  aubaine:         { metric: 'runs', threshold: 15 },
  // all-in / apogées du socle — se MÉRITENT au meilleur score (MOT-MIDTERM-001 conservé).
  perfectionniste: { metric: 'bestScore', threshold: 40000, or: [{ metric: 'runs', threshold: 16 }] },
  verre:           { metric: 'bestScore', threshold: 50000 },
  // Pactes — la contrepartie demande un build déjà mûr : seuils dans la bande des all-in,
  // avec la métrique de PRATIQUE du levier concerné en chemin quand elle a du sens.
  usurier:         { metric: 'bascules', threshold: 70, or: [{ metric: 'runs', threshold: 12 }] },
  monolithe:       { metric: 'bestScore', threshold: 65000, or: [{ metric: 'runs', threshold: 17 }] },
  serment_de_plomb:{ metric: 'zoneLines', threshold: 70, or: [{ metric: 'bestScore', threshold: 80000 }] },
  funambule:       { metric: 'bestScore', threshold: 130000 },
  gravats:         { metric: 'bestScore', threshold: 60000, or: [{ metric: 'runs', threshold: 18 }] },
  antimatiere:     { metric: 'bestScore', threshold: 90000 },
  obsidienne:      { metric: 'bestScore', threshold: 110000 },
  noyau:           { metric: 'bestScore', threshold: 150000 },
  tesseract:       { metric: 'bestScore', threshold: 200000 },
};

// Condition de déblocage d'une relique, ou `undefined` si starter (toujours offerte).
export function relicRequirement(id: string): UnlockReq | undefined { return UNLOCK_REQS[id]; }

// Une condition (principale ou alternative) est-elle remplie par les stats ?
function reqMet(stats: UnlockStats, metric: UnlockMetric, threshold: number): boolean {
  return (stats[metric] || 0) >= threshold;
}

// Détermine les reliques débloquées selon des stats cumulées (dérivé de UNLOCK_REQS).
// Une relique est débloquée si sa condition PRINCIPALE OU l'un de ses chemins ALTERNATIFS est atteint.
export function unlocksFor(stats: UnlockStats): string[] {
  const out: string[] = [];
  for (const id in UNLOCK_REQS) {
    const req = UNLOCK_REQS[id];
    if (reqMet(stats, req.metric, req.threshold) || (req.or || []).some(p => reqMet(stats, p.metric, p.threshold))) out.push(id);
  }
  return out;
}
